/**
 * Batch #14.37 — Composant générique pour guides web Mode d'emploi.
 *
 * Réutilise la même structure visuelle que ImmeubleGuide (hero + TOC sticky
 * + sections + Screenshot placeholders), mais piloté par une CONFIG passée
 * en prop. Cela permet de créer 8 guides en un fichier de config chacun
 * (~80 lignes) sans dupliquer 400 lignes de markup.
 *
 * Schéma de la config :
 * {
 *   slug:        "documents",
 *   label:       "Documents reçus",
 *   icon:        FileText,           // composant lucide-react
 *   accent:      "blue",             // tone Tailwind
 *   gradientFrom: "from-blue-600",
 *   gradientVia:  "via-blue-700",
 *   gradientTo:   "to-indigo-700",
 *   intro:       "Description courte affichée dans le hero",
 *   sections: [
 *     {
 *       id:       "introduction",
 *       title:    "Introduction",
 *       icon:     BookOpen,
 *       accent:   "blue",
 *       blocks: [
 *         { type: "paragraph", content: "Texte simple..." },
 *         { type: "tip", tone: "emerald", icon: CheckCircle2, content: "Astuce..." },
 *         { type: "screenshot", slug: "documents-list", alt: "Capture liste", caption: "..." },
 *         { type: "steps", items: [
 *             { title: "Étape 1", desc: "Description..." },
 *             ...
 *         ]},
 *         { type: "cards", columns: 3, items: [
 *             { title: "...", desc: "...", icon: "💳", tone: "emerald" },
 *             ...
 *         ]},
 *         { type: "list", items: ["Item 1", "Item 2"] },
 *       ]
 *     }
 *   ]
 * }
 */
import { useState, useEffect } from "react";
import DOMPurify from "dompurify";
import { ArrowLeft, Camera, Lightbulb, ChevronRight } from "lucide-react";

// Batch #14.288 (audit SEC-002) — tout HTML de config passe par DOMPurify (balises de mise en forme seulement)
const clean = (html) => DOMPurify.sanitize(String(html || ""), { ALLOWED_TAGS: ["strong", "b", "em", "i", "u", "a", "br", "code", "span"], ALLOWED_ATTR: ["href", "target", "rel", "class"] });
import { WEB_GUIDE_ILLUSTRATIONS } from "@/pages/portal/guides/illustrations/WebGuideIllustrations";
import { PAIE_GUIDE_ILLUSTRATIONS } from "@/pages/portal/guides/illustrations/PaieIllustrations";

const ALL_ILLUSTRATIONS = { ...WEB_GUIDE_ILLUSTRATIONS, ...PAIE_GUIDE_ILLUSTRATIONS };

// --------------- Helpers / sous-composants ---------------
function Section({ id, title, icon: Icon, accent = "cyan", children }) {
  return (
    <section id={id} className="scroll-mt-20" data-testid={`guide-section-${id}`}>
      <div className="flex items-center gap-3 mb-4 pb-3 border-b border-slate-200">
        <div className={`w-11 h-11 rounded-xl bg-${accent}-100 text-${accent}-700 flex items-center justify-center shrink-0`}>
          <Icon className="w-5 h-5" />
        </div>
        <h2 className="text-xl sm:text-2xl font-bold text-slate-900">{title}</h2>
      </div>
      <div className="space-y-4 text-[15px] text-slate-700 leading-relaxed">{children}</div>
    </section>
  );
}

function Tip({ children, tone = "amber", icon: Icon = Lightbulb }) {
  const tones = {
    amber:   { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-900", iconBg: "bg-amber-100 text-amber-700", label: "Astuce" },
    emerald: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-900", iconBg: "bg-emerald-100 text-emerald-700", label: "Bon à savoir" },
    rose:    { bg: "bg-rose-50", border: "border-rose-200", text: "text-rose-900", iconBg: "bg-rose-100 text-rose-700", label: "Important" },
  };
  const t = tones[tone] || tones.amber;
  return (
    <div className={`${t.bg} ${t.border} border rounded-xl p-4 flex items-start gap-3`}>
      <div className={`w-8 h-8 rounded-lg ${t.iconBg} flex items-center justify-center shrink-0`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className={`text-sm ${t.text} leading-relaxed`}>
        <div className="font-semibold mb-0.5">{t.label}</div>
        {children}
      </div>
    </div>
  );
}

function Step({ n, title, children, accent = "cyan" }) {
  return (
    <div className="flex gap-4">
      <div className={`w-9 h-9 rounded-full bg-${accent}-600 text-white font-bold flex items-center justify-center shrink-0 text-sm shadow-sm`}>
        {n}
      </div>
      <div className="flex-1 pt-1">
        <div className="font-semibold text-slate-800 mb-1">{title}</div>
        <div className="text-sm text-slate-600 leading-relaxed">{children}</div>
      </div>
    </div>
  );
}

function Screenshot({ slug, alt, caption, height = 220, guideSlug }) {
  const [hasImage, setHasImage] = useState(false);
  useEffect(() => {
    const img = new Image();
    img.onload = () => setHasImage(true);
    img.onerror = () => setHasImage(false);
    img.src = `${process.env.PUBLIC_URL || ""}/guides/${guideSlug}/${slug}.png`;
  }, [slug, guideSlug]);

  // Batch #14.38 — Illustration SVG inline si disponible (preferred sur le placeholder)
  const Illustration = ALL_ILLUSTRATIONS[slug];

  return (
    <figure className="my-3" data-testid={`guide-screenshot-${slug}`}>
      <div
        className="rounded-xl border-2 border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100 overflow-hidden shadow-sm"
      >
        {hasImage ? (
          <img
            src={`${process.env.PUBLIC_URL || ""}/guides/${guideSlug}/${slug}.png`}
            alt={alt}
            className="w-full h-auto"
          />
        ) : Illustration ? (
          <Illustration />
        ) : (
          <div className="h-full flex flex-col items-center justify-center text-slate-400 p-8" style={{ minHeight: `${height}px` }}>
            <Camera className="w-10 h-10 mb-2 opacity-50" />
            <div className="text-xs uppercase tracking-wider font-semibold mb-1">Capture d'écran à venir</div>
            <div className="text-sm text-center max-w-md italic">{alt}</div>
          </div>
        )}
      </div>
      {caption && <figcaption className="text-xs text-slate-500 mt-2 text-center italic">{caption}</figcaption>}
    </figure>
  );
}

function CardGrid({ items, columns = 3 }) {
  const gridClass = columns === 2 ? "grid-cols-1 sm:grid-cols-2"
                  : columns === 4 ? "grid-cols-2 sm:grid-cols-4"
                  : "grid-cols-1 sm:grid-cols-3";
  return (
    <div className={`grid ${gridClass} gap-3`}>
      {items.map((it, i) => {
        const TI = it.icon;
        // Mapping classnames pour éviter purge Tailwind
        const TONES = {
          emerald: { bg: "bg-emerald-50/60", border: "border-emerald-200", title: "text-emerald-900", desc: "text-emerald-800" },
          blue:    { bg: "bg-blue-50/60",    border: "border-blue-200",    title: "text-blue-900",    desc: "text-blue-800" },
          amber:   { bg: "bg-amber-50/60",   border: "border-amber-200",   title: "text-amber-900",   desc: "text-amber-800" },
          violet:  { bg: "bg-violet-50/60",  border: "border-violet-200",  title: "text-violet-900",  desc: "text-violet-800" },
          cyan:    { bg: "bg-cyan-50/60",    border: "border-cyan-200",    title: "text-cyan-900",    desc: "text-cyan-800" },
          rose:    { bg: "bg-rose-50/60",    border: "border-rose-200",    title: "text-rose-900",    desc: "text-rose-800" },
          indigo:  { bg: "bg-indigo-50/60",  border: "border-indigo-200",  title: "text-indigo-900",  desc: "text-indigo-800" },
          slate:   { bg: "bg-slate-50/60",   border: "border-slate-200",   title: "text-slate-900",   desc: "text-slate-700" },
        };
        const t = TONES[it.tone] || TONES.slate;
        return (
          <div key={i} className={`border ${t.border} ${t.bg} rounded-xl p-4`}>
            <div className="flex items-start gap-2 mb-1">
              {typeof it.icon === "string" ? (
                <span className="text-xl shrink-0">{it.icon}</span>
              ) : TI ? (
                <TI className={`w-5 h-5 ${t.title} shrink-0`} />
              ) : null}
              <div className={`font-semibold ${t.title} text-sm`}>{it.title}</div>
            </div>
            <p className={`text-xs ${t.desc} leading-snug`}>{it.desc}</p>
          </div>
        );
      })}
    </div>
  );
}

function Block({ block, accent, guideSlug }) {
  switch (block.type) {
    case "paragraph":
      // dangerouslySetInnerHTML pour permettre <strong>/<em>/<a> dans le contenu config
      return <p dangerouslySetInnerHTML={{ __html: clean(block.content) }} />;
    case "tip":
      return (
        <Tip tone={block.tone} icon={block.icon || Lightbulb}>
          <span dangerouslySetInnerHTML={{ __html: clean(block.content) }} />
        </Tip>
      );
    case "steps":
      return (
        <div className="space-y-3 mt-4">
          {block.items.map((s, i) => (
            <Step key={i} n={i + 1} title={s.title} accent={accent}>
              <span dangerouslySetInnerHTML={{ __html: clean(s.desc) }} />
            </Step>
          ))}
        </div>
      );
    case "cards":
      return <CardGrid items={block.items} columns={block.columns || 3} />;
    case "screenshot":
      return <Screenshot slug={block.slug} alt={block.alt} caption={block.caption} height={block.height} guideSlug={guideSlug} />;
    case "list":
      return (
        <ul className="list-disc pl-6 space-y-1.5 text-[14px]">
          {block.items.map((it, i) => (
            <li key={i} dangerouslySetInnerHTML={{ __html: clean(it) }} />
          ))}
        </ul>
      );
    default:
      return null;
  }
}

// --------------- Composant principal ---------------
export default function GenericWebGuide({ config, onBack }) {
  const [activeId, setActiveId] = useState(config.sections[0]?.id);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActiveId(e.target.id);
        });
      },
      { rootMargin: "-30% 0px -60% 0px" },
    );
    config.sections.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [config.sections]);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const HeroIcon = config.icon;

  return (
    <div className="max-w-6xl mx-auto" data-testid={`guide-${config.slug}-page`}>
      <div className="flex items-center gap-3 mb-5">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 transition"
          data-testid="guide-back-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Retour aux guides
        </button>
      </div>

      {/* Hero */}
      <div className={`bg-gradient-to-br ${config.gradientFrom || "from-cyan-600"} ${config.gradientVia || "via-cyan-700"} ${config.gradientTo || "to-teal-700"} rounded-2xl shadow-lg overflow-hidden mb-6`}>
        <div className="px-6 py-8 sm:px-10 sm:py-12 text-white">
          <div className="flex items-start gap-4 mb-3">
            <div className="w-14 h-14 rounded-2xl bg-white/15 backdrop-blur-sm flex items-center justify-center shrink-0">
              <HeroIcon className="w-7 h-7" />
            </div>
            <div>
              <div className="text-white/80 text-xs uppercase tracking-wider font-semibold">Mode d'emploi</div>
              <h1 className="text-2xl sm:text-3xl font-bold leading-tight">{config.label}</h1>
            </div>
          </div>
          <p className="text-white/90 text-[15px] leading-relaxed max-w-2xl">{config.intro}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-8">
        {/* TOC */}
        <aside className="hidden lg:block">
          <nav className="sticky top-20 space-y-1" data-testid={`guide-${config.slug}-toc`}>
            <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-2 px-3">
              Table des matières
            </div>
            {config.sections.map((s) => {
              const SIcon = s.icon;
              const isActive = activeId === s.id;
              return (
                <button
                  key={s.id}
                  onClick={() => scrollTo(s.id)}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm transition flex items-center gap-2 ${
                    isActive
                      ? `bg-${config.accent}-50 text-${config.accent}-700 font-semibold border-l-2 border-${config.accent}-600`
                      : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                  }`}
                  data-testid={`guide-${config.slug}-toc-${s.id}`}
                >
                  <SIcon className={`w-4 h-4 shrink-0 ${isActive ? `text-${config.accent}-600` : "text-slate-400"}`} />
                  <span className="flex-1 truncate">{s.label || s.title}</span>
                  {isActive && <ChevronRight className={`w-3 h-3 text-${config.accent}-600`} />}
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Contenu */}
        <article className="space-y-10 pb-10">
          {/* TOC mobile */}
          <div className="lg:hidden bg-slate-50 rounded-xl p-4 border border-slate-200" data-testid={`guide-${config.slug}-toc-mobile`}>
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Table des matières</div>
            <div className="flex flex-wrap gap-1.5">
              {config.sections.map((s) => (
                <button
                  key={s.id}
                  onClick={() => scrollTo(s.id)}
                  className="px-2.5 py-1 rounded-full bg-white border border-slate-200 text-[11px] text-slate-700 hover:border-slate-400"
                >
                  {s.label || s.title}
                </button>
              ))}
            </div>
          </div>

          {config.sections.map((sec) => (
            <Section
              key={sec.id}
              id={sec.id}
              title={sec.title}
              icon={sec.icon}
              accent={sec.accent || config.accent}
            >
              {(sec.blocks || []).map((b, i) => (
                <Block key={i} block={b} accent={sec.accent || config.accent} guideSlug={config.slug} />
              ))}
            </Section>
          ))}

          {/* Footer */}
          <div className="mt-10 pt-6 border-t border-slate-200 text-center">
            <p className="text-sm text-slate-500">
              📘 Ce guide est mis à jour régulièrement. Une question ?
              Contactez-nous via la tuile <strong>« Chat »</strong> ou par courriel.
            </p>
            <button
              onClick={onBack}
              className={`mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-${config.accent}-600 text-white text-sm font-medium hover:bg-${config.accent}-700`}
              data-testid="guide-footer-back-btn"
            >
              <ArrowLeft className="w-4 h-4" /> Retour aux autres guides
            </button>
          </div>
        </article>
      </div>
    </div>
  );
}
